import React, { useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { cloneDeep } from "lodash";
import { getUpdatedData as inboundUpdatedData } from './NewRecord';
import { getUpdatedData as outboundUpdatedData } from './NewRecordOutbound';
import { getRecordInsertingPosition } from "./CopyAndPaste";
import { RECORD_TYPE } from '../../../constants/datatypes';
import { treeActions } from '../../../redux/slices/treeSlice';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { ParentScreenCtx } from "/src/pages/workbench_build/WorkbenchBuild";
import { genKey } from '../../../utils/utilFunctions';
import { t } from 'i18next';

function NewElement({ openModal, toggle }) {
    const { t } = useTranslation();
    const contentRef = useRef();
    const dispatch = useDispatch();
    const parentScreen = useContext(ParentScreenCtx);
    const { rightTree } = useSelector(state => state.treeData[parentScreen]);
    const currentTab = rightTree.currentTab;
    const selectedNode = rightTree.tree[currentTab].selectedNode;
    const templateHeader = rightTree.tree.templateHeader;
    const content = t('BOX_LIKE_TO_PLACE_ELEM_INSIDE_CURR_ELEM_OR_DIRECTLY_AFTER').split(`\\n`).join("<br/>");
    const [showModal, setShowModal] = useState(false);

    const getNewElementData = (data, key, isNested) => {
        const title = getNewElementName(rightTree.tree);

        if (!data.length) return { key: `${key}-0`, title: title };

        let placingPosition, newElementKey;
        const details = getRecordInsertingPosition(data, selectedNode, isNested, parentScreen, currentTab);
        placingPosition = details.index;
        newElementKey = details.key;

        return { key: newElementKey, title: title, placingPosition: placingPosition };
    }

    const createNewElement = (isNested) => {
        let levels = selectedNode?.key?.split('-');
        levels = levels.splice(1);
        const translatedData = cloneDeep(rightTree.tree[currentTab].translatedData);

        function insertElementInNestedTree(translatedData, levels, isNested) {
            const level = isNested ? 0 : 1;
            if (levels.length === level) {
                const { children, key } = translatedData;
                const keyType = parentScreen === 'inbound' ?  'ib' : {header: "obh-0", body: "ob-0", trailer: "obt-0"}[currentTab]
                const newElementDetails = children ? getNewElementData(children, key, isNested) : getNewElementData(translatedData, keyType, isNested);

                const newElement = {
                    // Will be taken care after knowing proper usage of this
                    // GUI_XML_SORT_ORDER: newElementDetails.placingPosition,
                    nodeId:genKey(newElementDetails.title),
                    alternateNodeId:genKey(newElementDetails.title),
                    ICON_DISPLAY: 'ELEMENT',
                    // DESCRIPTION: newElementDetails.title,
                    ELEMENT_NAME: newElementDetails.title,
                    // MAPNAME: newElementDetails.title,
                    // LENGTH: 0,
                    // TYPE: newElementDetails.title,
                    FIELDTYPE: "ALPHA",
                    children: [],
                    key: newElementDetails.key,
                    title: `<${newElementDetails.title}>`,
                    type: 'ELEMENT'
                }
                if(parentScreen === 'outbound'){
                    newElement.PADDIR = 'RIGHT'
                    newElement.FILLCHAR = ' '
                } 
                const translatedDataArray = children ? translatedData.children : translatedData['children'] = [];
                translatedDataArray?.splice(newElementDetails.placingPosition, 0, newElement);
                dispatch(treeActions.setRightTreeSelectedNode({ 
                    screen: parentScreen,
                    type: currentTab, 
                    data: { ...newElement } 
                }));
                return;
            }

            const isArray = Array.isArray(translatedData);
            if (isArray) {
                const nestedData = translatedData[[levels[0]]];
                const newLevels = levels.splice(1);
                insertElementInNestedTree(nestedData, newLevels, isNested);
            } else {
                insertElementInNestedTree(translatedData.children, levels, isNested);
            }
        }

        insertElementInNestedTree(translatedData, levels, isNested);
        const data = parentScreen === 'inbound' ? inboundUpdatedData : outboundUpdatedData
        const updatedData = data(translatedData, templateHeader?.FILETYPE, null, parentScreen === 'workflow', currentTab);
        dispatch(treeActions.setNewRightTreeTranslatedData({ 
            screen: parentScreen,
            type: currentTab, 
            data: updatedData 
        }));
        toggle(prev => null);
        setShowModal(false);
    }

    const newElementHandler = () => {
      if (parentScreen === "outbound" && templateHeader.FILETYPE === "XML") {
        if((selectedNode?.key === 'ob-0' || selectedNode?.key === 'obh-0' || selectedNode?.key === 'obt-0')){
            createNewElement(true);
            return;
        } else if ((['ATTRIBUTE','SEQUENCE','PLUGIN','ACTION','COMMENT','TRACKING_REPORT','VARIABLE','VARIABLE_COLLECTION'].includes(selectedNode.ICON_DISPLAY) || (selectedNode.ICON_DISPLAY === 'QUERY' && selectedNode?.TYPE === 'QUERY')) && openModal) {
            createNewElement(false);
            return;
          } else {
            setShowModal(true);
          }
      } else {
        if (
          templateHeader.FILETYPE === "XML" &&
          selectedNode?.key === "ib-0-0"
        ) {
          createNewElement(true);
          return;
        } else if (!RECORD_TYPE[selectedNode.ICON_DISPLAY] && openModal) {
          createNewElement(false);
          return;
        } else if (
          selectedNode.key === "ib-0" &&
          currentTab !== "body" &&
          openModal
        ) {
          createNewElement(true);
          return;
        } else {
          setShowModal(true);
        }
      }
    }

    setTimeout(() => {
        if (contentRef.current?.innerHTML === "") contentRef.current.innerHTML = content
    }, 0)

    useEffect(() => {
        if (openModal) newElementHandler();
    }, [openModal]);

    return (
        <>
            <Dialog open={showModal} maxWidth="xs" fullWidth>
                <DialogTitle className="dialog-header">
                    <div className="title">
                        {t("BOX_CONFIRM")}
                    </div>
                </DialogTitle>
                <DialogContent className="dialog-content" dividers>
                    <div ref={contentRef}></div>
                </DialogContent>
                <DialogActions>
                    <Button size='small' variant="outlined" color="primary" onClick={() => createNewElement(true)} >
                        {t("BTN_YES")}
                    </Button>
                    <Button size='small' variant="outlined" color="primary" onClick={() => createNewElement(false)} >
                        {t("BTN_NO")}
                    </Button>
                    <Button size='small' variant="outlined" color="primary" onClick={() => {
                        toggle(null);
                        setShowModal(false);
                    }} >
                        {t("BTN_CANCEL")}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default NewElement

export const getNewElementName = (tree) => {
    const data = cloneDeep(tree);
    const body = data.body.translatedData;
    let titleNumber = 0;

    const getTitleName = (data) => {
        data.forEach((element) => {
            const name = element?.TYPE || element?.ELEMENT_NAME || element?.MAPNAME ; 
            if ((RECORD_TYPE[element.ICON_DISPLAY] || element.ICON_DISPLAY === 'REPEATING_ELEMENT') && name?.startsWith(t('UNNAMED'))) {
                let title = name;
                const indexOfUnderScore = title.indexOf('_');
                title = indexOfUnderScore >= 0 ? title.slice(0, indexOfUnderScore) : title;
                const latestUnnamedElement = + title.slice(t('UNNAMED').length);

                if (!isNaN(latestUnnamedElement)) {
                    titleNumber = latestUnnamedElement > titleNumber ? latestUnnamedElement : titleNumber;
                }
            }
            if (element?.children?.length > 0) {
                getTitleName(element.children);
            }
        });
    }

    getTitleName(body);

    return `${t('UNNAMED')}${Number(titleNumber) + 1}`
}
